import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, startOfDay, endOfDay, addDays, differenceInDays } from "date-fns";
import { DateRange } from "react-day-picker";
import {
  ResponsiveFilterToolbar,
  MobileFilterItem,
  useResponsiveFilterStyles,
  useActiveFilters,
} from "@/components/responsive-filter-toolbar";
import { useIsMobile } from "@/hooks/use-mobile";

interface DivergencesFilterToolbarProps {
  onFilterChange: (filters: { search?: string; from?: string; to?: string }) => void;
  defaultSearch?: string;
  defaultFrom?: string;
  defaultTo?: string;
}

export const DivergencesFilterToolbar: React.FC<DivergencesFilterToolbarProps> = ({
  onFilterChange,
  defaultSearch = "",
  defaultFrom,
  defaultTo,
}) => {
  const [search, setSearch] = useState(defaultSearch);
  const [displayedDateRange, setDisplayedDateRange] = useState<DateRange | undefined>(undefined);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (defaultFrom && defaultTo) {
      return {
        from: new Date(defaultFrom),
        to: new Date(defaultTo),
      };
    }

    if (defaultFrom) {
      return {
        from: new Date(defaultFrom),
        to: undefined,
      };
    }

    return undefined;
  });

  const [pendingDateRange, setPendingDateRange] = useState<DateRange | undefined>(undefined);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responsiveStyles = useResponsiveFilterStyles();
  const { inputStyles } = responsiveStyles;
  const isMobile = useIsMobile();

  const maxDateRange = 30;
  const DATE_FORMAT = "yyyy-MM-dd";
  const DISPLAY_DATE_FORMAT = "MMM dd, yyyy";

  const activeFilters = useActiveFilters({
    search,
    dateRange: dateRange?.from ? dateRange : undefined,
  });

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      onFilterChange({
        search: search || undefined,
        from: dateRange?.from ? format(dateRange.from, DATE_FORMAT) : undefined,
        to: dateRange?.to ? format(dateRange.to, DATE_FORMAT) : undefined,
      });
    }, 400);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  useEffect(() => {
    if (!dateRange) return;

    onFilterChange({
      search: search || undefined,
      from: dateRange?.from ? format(dateRange.from, DATE_FORMAT) : undefined,
      to: dateRange?.to ? format(dateRange.to, DATE_FORMAT) : undefined,
    });
  }, [dateRange]);

  useEffect(() => {
    if (defaultFrom || defaultTo) {
      const range: DateRange = {
        from: defaultFrom ? startOfDay(new Date(defaultFrom)) : undefined,
        to: defaultTo ? endOfDay(new Date(defaultTo)) : undefined,
      };

      setDateRange(range);
      setDisplayedDateRange(range);
      setPendingDateRange(range);
    }
  }, [defaultFrom, defaultTo]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleClear = () => {
    setSearch("");
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    if (!range) {
      setPendingDateRange(undefined);

      if (isMobile) {
        setDisplayedDateRange(undefined);
        setDateRange(undefined);
        setIsPopoverOpen(false);
      }

      return;
    }

    const normalizedRange: DateRange = {
      from: range.from ? startOfDay(range.from) : undefined,
      to: range.to ? endOfDay(range.to) : undefined,
    };

    if (normalizedRange.from && normalizedRange.to) {
      const daysDiff = differenceInDays(normalizedRange.to, normalizedRange.from) + 1;

      if (daysDiff < maxDateRange) {
        const minToDate = addDays(normalizedRange.from, maxDateRange - 1);
        normalizedRange.to = endOfDay(minToDate);
      }
    }

    setPendingDateRange(normalizedRange);

    if (isMobile && normalizedRange.from && normalizedRange.to) {
      setDisplayedDateRange(normalizedRange);
      setDateRange(normalizedRange);
      setIsPopoverOpen(false);
    }
  };

  const handleConfirmDateRange = () => {
    setDisplayedDateRange(pendingDateRange);
    setDateRange(pendingDateRange);
    setIsPopoverOpen(false);
  };

  const handleCancelDateRange = () => {
    setDisplayedDateRange(dateRange);
    setPendingDateRange(dateRange);
    setIsPopoverOpen(false);
  };

  const handleResetAllFilters = () => {
    setSearch("");
    setDisplayedDateRange(undefined);
    setDateRange(undefined);
    setPendingDateRange(undefined);
  };

  const handleClearDateRange = () => {
    setDisplayedDateRange(undefined);
    setDateRange(undefined);
    setPendingDateRange(undefined);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);

    if (open) {
      setPendingDateRange(dateRange);
    }
  };

  const isDateRangeDisabled = (date: Date): boolean => {
    if (date > new Date()) return true;

    const currentRange = pendingDateRange || dateRange;

    if (currentRange?.from) {
      if (date < currentRange.from) return true;

      const minToDate = addDays(currentRange.from, maxDateRange - 1);

      if (date < minToDate) return true;
    }

    return false;
  };

  const handleShowResults = () => {
    onFilterChange({
      search: search || undefined,
      from: dateRange?.from ? format(dateRange.from, DATE_FORMAT) : undefined,
      to: dateRange?.to ? format(dateRange.to, DATE_FORMAT) : undefined,
    });
  };

  return (
    <ResponsiveFilterToolbar
      title="Filter Divergences"
      hasActiveFilters={activeFilters.hasActiveFilters}
      activeFiltersCount={activeFilters.activeFiltersCount}
      onShowResults={handleShowResults}
      onResetFilters={handleResetAllFilters}
      showButtonDisabled={!activeFilters.hasActiveFilters}
    >
      <MobileFilterItem label="Search">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Filter divergences..."
            value={search}
            onChange={handleSearchChange}
            className={inputStyles}
          />

          {search && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
              onClick={handleClear}
              tabIndex={-1}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </MobileFilterItem>

      <MobileFilterItem label="Date Range">
        <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                responsiveStyles.buttonStyles,
                "justify-start text-left font-normal flex items-center w-full max-w-[320px]",
                !dateRange?.from && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-sm" />

              <div className="flex justify-between items-center w-full relative pr-8 lg:pr-0">
                {displayedDateRange?.from ? (
                  <span className="truncate">
                    {displayedDateRange?.to
                      ? `${format(displayedDateRange.from, DISPLAY_DATE_FORMAT)} - ${format(
                          displayedDateRange.to,
                          DISPLAY_DATE_FORMAT
                        )}`
                      : format(displayedDateRange.from, DISPLAY_DATE_FORMAT)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select date range</span>
                )}

                {displayedDateRange?.from && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearDateRange();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearDateRange();
                      }
                    }}
                    className="cursor-pointer ml-2"
                  >
                    <X className="h-3 w-3" />
                  </span>
                )}
              </div>
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 border-b text-sm text-muted-foreground">
              Select date range - {maxDateRange} days max
            </div>

            <Calendar
              autoFocus
              mode="range"
              selected={pendingDateRange}
              onSelect={handleDateRangeSelect}
              disabled={isDateRangeDisabled}
              numberOfMonths={2}
            />

            {!isMobile && (
              <div className="flex justify-end gap-2 p-3 border-t">
                <Button variant="outline" size="sm" onClick={handleCancelDateRange}>
                  Cancel
                </Button>

                <Button size="sm" onClick={handleConfirmDateRange} disabled={!pendingDateRange?.from}>
                  Apply
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </MobileFilterItem>
    </ResponsiveFilterToolbar>
  );
};
